import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_HOST, CSRF_COOKIE_NAME } from 'src/constants/environment';
import { ANALYTICS_ANONYMOUS_ID } from 'src/constants/localStorage';
import {
    ACCESS_TOKEN_KEY,
    ACCESS_TOKEN_SOURCE_KEY,
    IMPERSONATED_KEY,
} from 'src/constants/sessionStorage';
import Cookies from 'universal-cookie';
import { Omit } from '../helpers/typescript';
import APIClient, { UploadedFile } from './APIClient';
import { buildLogoutUrls } from './logoutHelpers';

const cookies = new Cookies();

export interface APIContextProps {
    apiClient: APIClient;
    signOut: (redirectTo?: string) => void;
}

const context = React.createContext<APIContextProps | null>(null);

export const withAPI = <P extends APIContextProps, R = Omit<P, keyof APIContextProps>>(
    Component: React.ComponentType<P>,
) => {
    return (props: R) => {
        return (
            <context.Consumer>
                {(apiContext) => (
                    <Component {...(props as any)} apiClient={apiContext!.apiClient} />
                )}
            </context.Consumer>
        );
    };
};

export const Provider = ({ children }: { children: React.ReactNode }) => {
    const apiClient = useMemo(() => new APIClient(API_HOST + '/api', CSRF_COOKIE_NAME), []);
    const signOut = useCallback(async (redirectTo?: string) => {
        try {
            const currentOrigin = typeof window === 'undefined' ? API_HOST : window.location.origin;
            const logoutUrls = buildLogoutUrls(API_HOST, currentOrigin);

            await Promise.allSettled(
                logoutUrls.map((logoutUrl) => axios.post(logoutUrl, {}, { withCredentials: true })),
            );
        } finally {
            const rootDomain = document.domain.replace(/^\w+\./, '.');
            const options = {
                path: '/',
                domain: rootDomain,
            };
            cookies.remove(CSRF_COOKIE_NAME, options);

            sessionStorage.removeItem(IMPERSONATED_KEY);
            sessionStorage.removeItem(ACCESS_TOKEN_KEY);
            sessionStorage.removeItem(ACCESS_TOKEN_SOURCE_KEY);
            sessionStorage.removeItem('invited_vendor_token');
            sessionStorage.removeItem('mobile_login');
            sessionStorage.removeItem('mobile_redirect_uri');
            sessionStorage.removeItem('mobile_app_type');
            sessionStorage.removeItem('mobile_email');
            localStorage.removeItem(ANALYTICS_ANONYMOUS_ID);

            window.location.href = redirectTo ?? '/login';
        }
    }, []);

    // useMemo required - context value passed to provider.
    // Without stable reference, all context consumers re-render on every parent render.
    const contextValue = useMemo(() => ({ apiClient, signOut }), [apiClient, signOut]);

    return <context.Provider value={contextValue}>{children}</context.Provider>;
};

function useValue() {
    const value = useContext(context);
    if (!value) {
        throw new Error('APIProvider is not detected.');
    }
    return value;
}

export function useAPI() {
    const { apiClient } = useValue();
    return apiClient;
}

export function useSignOut() {
    const { signOut } = useValue();
    return signOut;
}

export function useUploadFiles() {
    const { t } = useTranslation('validation');
    const { enqueueSnackbar } = useSnackbar();
    const api = useAPI();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    // Keep useCallback - this is part of the hook's public API
    const upload = useCallback(
        async (files: Array<File>) => {
            setLoading(true);
            setError(null);
            try {
                const uploadedFiles = await Promise.all(files.map((file) => api.uploadFile(file)));
                setUploadedFiles(uploadedFiles);
                return uploadedFiles;
            } catch (err) {
                const error = t('unknownErrorMessage');
                setError(error);
                enqueueSnackbar(error, { variant: 'error' });
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [api, enqueueSnackbar, t],
    );
    const result = {
        loading,
        error,
        uploadedFiles,
    };
    return [upload, result] as const;
}
